import { filter, find, toPath, set } from 'lodash-es';
import modelLoader from '../models';
import {
  RhinoAttribute,
  RhinoModelKind,
  RhinoModuleInfo,
  RhinoRecordIdentifier,
  RhinoResource
} from '../index';

const apiSchemas = () => modelLoader.resources;

export const getModel = (model: string): RhinoResource =>
  apiSchemas()[model] as RhinoResource;

export const getParentModel = (model: RhinoResource): RhinoResource | null => {
  if (model.ownedBy === 'global' || model.ownedBy === null) return null;

  return getModelFromRef(model.properties[model.ownedBy]);
};

export const getModelAncestors = (model: RhinoResource): RhinoResource[] => {
  const result: RhinoResource[] = [];
  let current = getParentModel(model);
  while (current != null) {
    result.push(current);
    current = getParentModel(current);
  }
  return result;
};

export const isOwnerGlobal = (model: RhinoResource): boolean => {
  let current: RhinoResource | null = model;
  while (current && current.ownedBy !== null && current.ownedBy !== 'global') {
    current = getParentModel(current);
  }

  return current?.ownedBy === 'global';
};

export const getBaseOwnerFilters = (
  model: RhinoResource,
  baseOwnerId?: RhinoRecordIdentifier
): Record<string, unknown> => {
  const filters: Record<string, unknown> = {};
  const pathToRoot = getModelAncestors(model);
  const rootModel = pathToRoot[pathToRoot.length - 1];
  // if the root model is globally owned, we don't want to add any filters. these models have 'global' as parent.
  // only the base owner will have null as parent.
  if (baseOwnerId && rootModel && rootModel.ownedBy == null) {
    const modelNames = pathToRoot.map((el) => el.model);
    set(filters, modelNames, baseOwnerId);
  }
  return filters;
};

export const getCreatableAttributes = (
  model: RhinoResource
): RhinoAttribute[] => filter(model.properties, (a) => a.creatable);
export const getUpdatableAttributes = (
  model: RhinoResource
): RhinoAttribute[] => filter(model.properties, (a) => a.updatable);

export const isReference = (attribute: RhinoAttribute) =>
  attribute.type === 'reference';
export const getReferenceAttributes = (
  model: RhinoResource
): RhinoAttribute[] => filter(model.properties, (a) => isReference(a));

export const isIdentifier = (attribute: RhinoAttribute) =>
  attribute.format === 'identifier';
export const getIdentifierAttribute = (
  model: RhinoResource
): RhinoAttribute | undefined => find(model.properties, (a) => isIdentifier(a));

export const getModuleInfo = (module: string): RhinoModuleInfo | undefined =>
  modelLoader.modules[module];

export const authOwnerProperty = () => getModuleInfo('rhino')?.authOwner;
export const authOwnerModel = () => getModel(authOwnerProperty() as string);

export const baseOwnerProperty = () => getModuleInfo('rhino')?.baseOwner;
export const baseOwnerModel = () => getModel(baseOwnerProperty() as string);
export const isBaseOwned = (model: RhinoResource): boolean => {
  if (model.ownedBy === 'global' || model.ownedBy === null) return false;

  return (
    getModelFromRef(model.properties[model.ownedBy])!.model ===
    baseOwnerProperty()
  );
};
// Which party owns the class, as the server exports it: engine-owned system
// models under /api/_rhino, the core models a host subclasses (User,
// Organization, roles), or the host's own. Absent means app.
export const modelKind = (model: RhinoResource): RhinoModelKind =>
  model.kind ?? 'app';
export const isSystemModel = (model: RhinoResource): boolean =>
  modelKind(model) === 'system';
export const isCoreModel = (model: RhinoResource): boolean =>
  modelKind(model) === 'core';
export const isAppModel = (model: RhinoResource): boolean =>
  modelKind(model) === 'app';

export const getBaseOwnedModels = (): RhinoResource[] =>
  filter(
    apiSchemas(),
    (m) => isAppModel(m as RhinoResource) && isBaseOwned(m as RhinoResource)
  ) as RhinoResource[];

export const getOwnedModels = (model: RhinoResource): RhinoResource[] =>
  filter(
    apiSchemas(),
    (m) => (m as RhinoResource).ownedBy === model.model
  ) as RhinoResource[];

export const getModelFromRef = (
  attribute: RhinoAttribute
): RhinoResource | null => {
  if (!attribute) return null;

  // Reference extraction walks heterogeneous OpenAPI shapes ($ref, anyOf,
  // items); treat the attribute loosely for the lookup.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const attr = attribute as any;
  const ref =
    // Direct ref
    attr['$ref'] ||
    // anyOf ref, probably because its nullable
    attr?.anyOf?.[0]?.['$ref'] ||
    // Any array of refs
    attr?.items?.['$ref'] ||
    // Any array of anyOf refs
    attr?.items?.anyOf?.[0]?.['$ref'];

  if (!ref) return null;

  const parts = ref.split('/');
  const refName = parts[parts.length - 1];

  const model = apiSchemas()[refName] as RhinoResource;

  console.assert(
    !!model,
    `Reference for attribute ${attribute.name} was not found`,
    attribute
  );

  return model;
};

export const getModelAndAttributeFromPath = (
  model: RhinoResource,
  path: string
): [
  RhinoResource | undefined,
  RhinoAttribute | undefined,
  string?,
  string?
] => {
  const split = toPath(path);
  const length = split.length;
  let current: RhinoResource | null = model;
  let lastModel: RhinoResource = model;
  let index = 0;
  let attribute: RhinoAttribute | null = null;
  let operator: string | null = null;
  const plainPath: string[] = [];

  while (current && index < length) {
    const [attributeName, operatorName] = split[index++].split('::');

    // A number means its an array indicator
    if (!isNaN(parseInt(attributeName))) {
      continue;
    }
    plainPath.push(attributeName);

    attribute = current.properties[attributeName];
    operator = operatorName;

    lastModel = current;
    if (!attribute)
      console.error(
        `Attribute name ${attributeName} is ${attribute} for model ${current.name} on path ${path}`
      );
    current = getModelFromRef(attribute);
  }

  return index && index === length
    ? [
        lastModel,
        attribute ?? undefined,
        operator ?? undefined,
        plainPath.join('.')
      ]
    : [undefined, undefined];
};

export const getAttributeFromPath = (model: RhinoResource, path: string) =>
  getModelAndAttributeFromPath(model, path)[1];

export const oauthProviders = () => getModuleInfo('rhino')?.oauth || [];

export const hasModule = (moduleName: string) =>
  getModuleInfo(moduleName) !== undefined;

// Org-backed = base owner differs from auth owner. (Phase 2 redefines this to
// `!single_user_mode` once that flag exists; the call sites that gate org UI on
// it then become single-user-aware without changes.)
export const hasOrganizationsModule = () => {
  const m = getModuleInfo('rhino');
  return !!m && m.baseOwner !== m.authOwner;
};
export const notificationsEnabled = (): boolean =>
  getModuleInfo('rhino')?.notifications_enabled ?? false;
export const hasSubscriptionsModule = () => hasModule('rhino_subscriptions');

export const allowOrgCreation = (): boolean =>
  getModuleInfo('rhino')?.allow_org_creation ?? false;

export const hasStripe = () => Boolean(getModuleInfo('rhino')?.stripe_enabled);

// When true, the host gates the app on an active subscription (dormant
// paywall). Absent/false => freemium, so callers default to permissive.
export const requireActiveSubscription = () =>
  Boolean(getModuleInfo('rhino')?.require_active_subscription);

// The API reads order as one comma-separated string, but a type can only check
// the terms — the union of every permutation of a joined string is
// combinatorial. Callers pass terms and the boundary joins them, so the sieve
// still receives the spelling it parses.
